import '@/styles/main.scss';
import { type ComponentPublicInstance, createApp } from 'vue';
import { createPinia } from 'pinia';
import 'anylogger-console';
import App from '@/App.vue';
import router from '@/router';
import { registerAppComponents } from '@/plugins/appComponents';
import { registerLoggingFilters } from '@/plugins/loggingFilters';

import { defineRules } from '@/plugins/vee-validation/vee-validation';
import { useAppErrorStore } from '@/stores/appErrorStore';
import { registerVuetify } from '@/plugins/vuetify';
import { configureAmplitude } from '@/plugins/amplitude';
import { useAmplitudeFeatures } from '@/stores/featureToggles/amplitudeFeaturesStore';
import { taggedLogger } from '@/util/taggedLogger';

const log = taggedLogger('main');
log.info(
    `Loading app with environment variables: \n meta.end = ${JSON.stringify(import.meta.env)}`,
);

if (import.meta.env.MODE === 'mocked-api') {
    log.info('Dev mocked-api mode');
    const { worker } = await import('@mocks/browser');
    await worker.start();
}

defineRules();
// Never awaited: app startup must not be gated on a third-party analytics service being
// reachable. The feature toggles resolve off this promise once the SDK is up.
const amplitudeReady = configureAmplitude();

/**
 * app variable is named `Vue` to resolve an issue in pycharm where
 * the registered components and plugins were not being inferred properly.
 */
const Vue = createApp(App);
Vue.use(createPinia());
Vue.use(router);
Vue.use(registerVuetify());
Vue.use(registerAppComponents);
Vue.use(registerLoggingFilters);
Vue.config.errorHandler = (
    err: unknown,
    instance: ComponentPublicInstance | null,
    info: string,
) => {
    useAppErrorStore().handleVueError(err, instance, info);
};

// Placed after createPinia() so the store has an active Pinia instance without relying on
// microtask ordering. The chain must be terminated: refresh() cannot reject, but
// configureAmplitude() can, and an unhandled rejection would be fed straight back into the
// SDK's own error tracking.
amplitudeReady
    .then(() => useAmplitudeFeatures().refresh())
    .catch((error) => log.error('Amplitude bootstrap failed; feature toggles stay off', error));

Vue.mount('#app');
